import React, { forwardRef, useEffect, useState } from "react";
import { Row, Col, Switch } from "antd";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import Surface from "../../../components/layouts/Surface";
import SwitchItem from "../../../components/forms/SwitchItem";
import FormHeader from "../../../components/forms/FormHeader";
import {
  SettingsDataSelect,
  BranchSelect,
  MetaDataSelect,
} from "../../../components/forms/AutoCompleteFormItem";
import Spacer from "../../../components/layouts/Spacer";
import ResponsiveItemsWrapper from "../../../components/layouts/ResponsiveItemsWrapper";
import { SelectItem as SelectItemComponent } from "../../../components/forms/FormItems";
import moment from "moment";
import { useSelector } from "react-redux";

var unflatten = require("flat").unflatten;

const FinanceInfo = forwardRef((props, ref) => {
  const { activeEmployee, values, waitingForExport, onExportChange } = props;
  const intl = useIntl();
  const deepChanges = unflatten(values, {
    safe: true,
  });
  const mergedValues = { ...activeEmployee, ...deepChanges };

  const filterCostStrategy = (items) => {
    let toFilter = [];
    if (!mergedValues.daily_transportation_cost_id) {
      toFilter.push("enforce_daily");
    }
    if (!mergedValues.monthly_transportation_cost_id) {
      toFilter.push("enforce_monthly");
    }
    return items.filter((i) => !toFilter.includes(i.value));
  };

  let age = null;
  if (activeEmployee && activeEmployee.birthday) {
    var today = new Date();
    var birthDate = new Date(activeEmployee.birthday);
    age = today.getFullYear() - birthDate.getFullYear();
    var m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
  }

  const activeTariffFilter = {
    $and: [
      {
        $or: activeEmployee.tariff_id
          ? [{ active: true }, { id: activeEmployee.tariff_id }]
          : [{ active: true }],
      },
    ],
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Surface>
        <FormHeader
          title={<FormattedMessage id="app.employees.financeInfo" />}
        />
        <Row>
          <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="finance_info.exported_to_finance"
                  category="employee"
                />
              </div>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="finance_info.has_pension"
                  category="employee"
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="finance_info.pay_via_bank"
                  category="employee"
                />
              </div>

              <div style={{ flex: 1 }}>
                <SwitchItem
                  name="finance_info.compensation_paid"
                  category="employee"
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SwitchItem name="oncall" category="employee" />
              </div>
              <div style={{ flex: 1 }}>
                <SwitchItem name="old_age_allowance" category="employee" />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                {((age && age >= 67) ||
                  (activeEmployee.employee_type_id === 2 &&
                    !activeEmployee.foreign.tourist_with_license)) && (
                    <SwitchItem
                      name="pay_transportation_costs_over_75"
                      category="employee"
                    />
                  )}
              </div>

              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "start",
                  }}
                >
                  <div className="ant-col ant-form-item-label">
                    <label>
                      {activeEmployee.exported_at
                        ? `${intl.formatMessage({
                          id: "app.forms.employee.exported_at",
                        })}: ${moment(activeEmployee.exported_at).format(
                          "l"
                        )}`
                        : intl.formatMessage({
                          id: "app.forms.employee.not_exported",
                        })}
                    </label>
                  </div>
                  <div className="ant-form-item-control-input">
                    <Switch
                      disabled={!activeEmployee.exported_at}
                      checked={waitingForExport}
                      onChange={onExportChange}
                    />
                  </div>
                  <div style={{ display: "none" }}>
                    <DatePickerItem name="exported_at" category="employee" />
                  </div>
                </div>
              </div>
            </ResponsiveItemsWrapper>
          </Col>

          <Col xs={24} sm={24} md={24} lg={24} xl={12} xxl={12}>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  name="accounting_info.number"
                  number={true}
                  category="employee"
                  required={
                    mergedValues.finance_info
                      ? mergedValues.finance_info.exported_to_finance
                      : false
                  }
                />
              </div>

              <Spacer horizontal={true} />

              <div style={{ flex: 1 }}>
                <BranchSelect
                  items={
                    activeEmployee && activeEmployee.salary_branch
                      ? [{ ...activeEmployee.salary_branch }]
                      : []
                  }
                  disabled={
                    activeEmployee.branch_active &&
                    activeEmployee.branch_active.length === 1
                  }
                  name="salary_branch_id"
                  category="employee"
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <InputItem
                  name="finance_info.pension_details"
                  category="employee"
                />
              </div>
              <Spacer horizontal={true} />

              <div style={{ flex: 1 }}>
                <SettingsDataSelect
                  name="tariff_id"
                  category="employee"
                  settingsdataName={"tariffs"}
                  paramName={"cost"}
                  extraParams={{
                    filter: activeTariffFilter,
                  }}
                  type="list"
                />
              </div>
            </ResponsiveItemsWrapper>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SettingsDataSelect
                  name="monthly_transportation_cost_id"
                  category="employee"
                  settingsdataName={
                    "system_tables/salary/monthly_transportation_cost"
                  }
                  paramName={"cost"}
                />
              </div>
              <Spacer horizontal={true} />

              <div style={{ flex: 1 }}>
                <SettingsDataSelect
                  name="daily_transportation_cost_id"
                  category="employee"
                  settingsdataName={
                    "system_tables/salary/daily_transportation_cost"
                  }
                  paramName={"cost"}
                />
              </div>
            </ResponsiveItemsWrapper>

            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SelectItemComponent
                  name="transportation_cost_strategy"
                  category="employee"
                  transportation={true}
                  filterItems={filterCostStrategy}
                />
              </div>
              <Spacer horizontal={true} />

              <div style={{ flex: 1 }}>
                <MetaDataSelect
                  name="religion_id"
                  category="employee"
                  metadataName={"religions"}
                  identfierId="id"
                />
              </div>
            </ResponsiveItemsWrapper>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                <SelectItemComponent
                  name="rest_day"
                  category="employee"
                  daysInt={true}
                  allowClear
                />
              </div>
              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}>
                <InputItem
                  name="oncall_hours"
                  number={true}
                  category="employee"
                  disabled={!mergedValues.oncall}
                  required={mergedValues.oncall}
                  min={0}
                  max={182}
                />
              </div>
            </ResponsiveItemsWrapper>
            <ResponsiveItemsWrapper>
              <div style={{ flex: 1 }}>
                {(activeEmployee.professional_role_id ||
                  values.professional_role_id) && (
                    <InputItem
                      required={true}
                      number={true}
                      min={0}
                      max={100}
                      name="professional_role_position_percentage"
                      category="employee"
                    />
                  )}
              </div>
              <Spacer horizontal={true} />
              <div style={{ flex: 1 }}></div>
            </ResponsiveItemsWrapper>
          </Col>
        </Row>
      </Surface>
    </div>
  );
});

export default FinanceInfo;
